import React, { useState, useRef, useEffect } from 'react';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Card } from "@/components/ui/card";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { FileText, Gavel, Euro, Shield, Info, CheckCircle2, Printer, FileDown } from "lucide-react";
import { cn } from "@/lib/utils";

interface ContractSection {
  id: string;
  title: string;
  icon: React.ReactNode;
  content: string | React.ReactNode;
  required?: boolean;
}

interface ContractViewerProps {
  contractType: 'main' | 'dataProcessing' | 'sepa';
  title: string;
  description: string;
  sections: ContractSection[];
  onScrollComplete?: () => void;
  showDownload?: boolean;
  onDownload?: () => void;
  isDownloading?: boolean;
}

const ContractViewer: React.FC<ContractViewerProps> = ({
  contractType,
  title,
  description,
  sections,
  onScrollComplete,
  showDownload = true,
  onDownload,
  isDownloading = false
}) => {
  const [expandedSections, setExpandedSections] = useState<string[]>([]);
  const [readSections, setReadSections] = useState<string[]>([]);
  const [scrollProgress, setScrollProgress] = useState(0);
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (sections.length > 0 && expandedSections.length === 0) {
      setExpandedSections([sections[0].id]);
    }
  }, [sections, expandedSections.length]);

  const handleScroll = () => {
    if (contentRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = contentRef.current;
      const progress = (scrollTop / (scrollHeight - clientHeight)) * 100;
      setScrollProgress(Math.min(progress, 100));
      
      if (progress >= 95 && onScrollComplete) {
        onScrollComplete();
      }
    }
  };

  const handleAccordionChange = (sectionId: string) => {
    setExpandedSections(prev => 
      prev.includes(sectionId) 
        ? prev.filter(id => id !== sectionId)
        : [...prev, sectionId]
    );
    
    setReadSections(prev => {
      if (!prev.includes(sectionId)) {
        return [...prev, sectionId];
      }
      return prev;
    });
  };

  const expandAll = () => {
    setExpandedSections(sections.map(s => s.id));
    setReadSections(sections.map(s => s.id));
  };

  const collapseAll = () => {
    setExpandedSections([]);
  };

  const allSectionsRead = readSections.length === sections.length;

  const getContractIcon = () => {
    switch (contractType) {
      case 'main': return <Gavel className="h-6 w-6" />;
      case 'dataProcessing': return <Shield className="h-6 w-6" />;
      case 'sepa': return <Euro className="h-6 w-6" />;
      default: return <FileText className="h-6 w-6" />;
    }
  };

  return (
    <Card className="overflow-hidden border border-border shadow-sm">
      <div className="p-4 md:p-6 bg-card border-b border-border">
        <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
          <div className="flex items-start gap-3 md:gap-4 flex-1">
            <div className="p-2 md:p-3 rounded-lg bg-muted text-muted-foreground flex items-center justify-center shrink-0">
              {getContractIcon()}
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-foreground mb-1">{title}</h3>
              <p className="text-sm text-muted-foreground">{description}</p>
            </div>
          </div>
          
          {showDownload && (
            <div className="flex gap-2 self-start">
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={onDownload}
                      disabled={isDownloading}
                      className="text-[#1967D2] border-[#1967D2]/30 hover:bg-[#1967D2]/5"
                    >
                      {isDownloading ? 'Generiere...' : <FileDown className="h-4 w-4 mr-2" />}
                      <span className="hidden sm:inline">PDF</span>
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>Als PDF herunterladen</TooltipContent>
                </Tooltip>
              </TooltipProvider>
              
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button variant="ghost" size="icon" onClick={() => window.print()} className="text-muted-foreground hover:text-foreground">
                      <Printer className="h-4 w-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>Drucken</TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
          )}
        </div>

        <div className="mt-4 md:mt-6">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-2">
            <span className="text-xs text-muted-foreground font-medium">
              {readSections.length} von {sections.length} Abschnitten gelesen
            </span>
            <div className="flex gap-2">
              <Button variant="ghost" size="sm" onClick={expandAll} className="h-7 text-xs px-2 text-muted-foreground hover:text-[#1967D2]">
                Alle öffnen
              </Button>
              <Button variant="ghost" size="sm" onClick={collapseAll} className="h-7 text-xs px-2 text-muted-foreground hover:text-[#1967D2]">
                Alle schließen
              </Button>
            </div>
          </div>
          <Progress 
            value={(readSections.length / sections.length) * 100} 
            className="h-1.5" 
            indicatorClassName={cn(allSectionsRead ? "bg-success" : "bg-[#1967D2]")}
          />
          {allSectionsRead && (
            <div className="flex items-center gap-1.5 mt-2 text-xs font-medium text-success-strong animate-in fade-in slide-in-from-left-2">
              <CheckCircle2 className="h-3.5 w-3.5" />
              Alle Abschnitte wurden gelesen
            </div>
          )}
        </div>
      </div>

      <div 
        ref={contentRef}
        onScroll={handleScroll}
        className="max-h-[400px] overflow-y-auto bg-muted/50 scroll-smooth"
      >
        <Accordion 
          type="multiple" 
          value={expandedSections} 
          onValueChange={setExpandedSections}
          className="w-full"
        >
          {sections.map((section, index) => (
            <AccordionItem 
              key={section.id} 
              value={section.id} 
              className={cn(
                "border-b border-border last:border-0 bg-card px-4 md:px-6",
                expandedSections.includes(section.id) && "bg-card"
              )}
            >
              <AccordionTrigger 
                onClick={() => {
                   if (!readSections.includes(section.id)) {
                     setReadSections(prev => [...prev, section.id]);
                   }
                }}
                className="hover:no-underline py-4"
              >
                <div className="flex items-center gap-3 md:gap-4 text-left">
                  <div className="text-muted-foreground shrink-0">
                    {section.icon}
                  </div>
                  <span className="text-sm md:text-base font-medium text-foreground flex-1 pr-4">
                    {section.title}
                  </span>
                  {readSections.includes(section.id) && (
                    <CheckCircle2 className="h-4 w-4 text-success-strong shrink-0 mr-2" />
                  )}
                </div>
              </AccordionTrigger>
              <AccordionContent className="pt-0 pb-4 md:pb-6 pl-9 md:pl-12 pr-2">
                <div className="text-sm text-muted-foreground leading-relaxed whitespace-pre-line">
                  {section.content}
                </div>
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>

      {scrollProgress < 95 && (
        <div className="bg-muted border-t border-border p-2 md:p-3 flex items-center justify-center gap-2 text-xs text-muted-foreground sticky bottom-0">
          <Info className="h-3.5 w-3.5" />
          Bitte scrollen Sie nach unten, um den gesamten Vertrag zu lesen
        </div>
      )}
    </Card>
  );
};

export default ContractViewer;
